/**
 * ผังต้นสกิล (รอบ 20) — จัดตำแหน่งอัตโนมัติจากข้อมูล ไม่มีพิกัดเขียนมือ (เพิ่มสกิลใน classes.json แล้วผังขยายเอง)
 *
 * คอลัมน์ = สาย (`ClassDef.branches` ตามลำดับ · สกิลไม่มีสายอยู่คอลัมน์แรก) แตกเป็นเลนย่อยเมื่อสายเดียวกันมีหลายสกิลในแถวเดียว
 * แถว = ความลึกของ `req` (สกิลพื้นฐานแถวบนสุด สกิลที่ต้องมีสกิลอื่นอยู่ใต้สกิลนั้นเสมอ) · ความลึกเท่ากันเรียงตาม `job`
 * เส้นเชื่อม = จาก req ไปสกิลที่ต้องการ · pure function มีเทสต์ (ไม่ทับกัน · req อยู่แถวบนกว่าเสมอ)
 */
import type { SkillDef } from "@wickhaven/shared";

export interface TreeNode { id: string; col: number; row: number }
export interface TreeLayout {
  nodes: TreeNode[];
  edges: Array<{ from: string; to: string }>;
  cols: number;
  rows: number;
  /** ป้ายหัวคอลัมน์ (ชื่อสาย) เริ่มที่คอลัมน์ไหน กว้างกี่เลน */
  headers: Array<{ label: string; col: number; span: number }>;
}

/** สกิลต่อแถวต่อสายสูงสุด (หน้าต่างแคบ) เกินนี้เลื่อนลงแถวถัดไป */
const MAX_LANES = 3;

function reqsOf(s: SkillDef): string[] {
  if (!s.req) return [];
  return (Array.isArray(s.req) ? s.req : [s.req]).map((r) => r.skill);
}

export function layoutSkillTree(skills: readonly SkillDef[], branches: Readonly<Record<string, string>> = {}): TreeLayout {
  const byId = new Map(skills.map((s) => [s.id, s]));
  const depth = new Map<string, number>();
  const depthOf = (id: string, seen = new Set<string>()): number => {
    const hit = depth.get(id);
    if (hit !== undefined) return hit;
    if (seen.has(id)) return 0; // วงวนในข้อมูล (ไม่ควรมี) — ไม่ค้าง
    seen.add(id);
    const s = byId.get(id);
    const inside = s ? reqsOf(s).filter((r) => byId.has(r)) : [];
    const d = inside.length ? 1 + Math.max(...inside.map((r) => depthOf(r, seen))) : 0;
    depth.set(id, d);
    return d;
  };
  for (const s of skills) depthOf(s.id);

  const order = ["", ...Object.keys(branches)];
  const groups = new Map<string, SkillDef[]>();
  for (const s of skills) {
    const b = s.branch && branches[s.branch] ? s.branch : "";
    groups.set(b, [...(groups.get(b) ?? []), s]);
  }
  const nodes: TreeNode[] = [];
  const finalRow = new Map<string, number>();
  const headers: TreeLayout["headers"] = [];
  let col = 0;
  for (const b of order) {
    const list = groups.get(b);
    if (!list?.length) continue;
    // วางทีละสกิล (เรียงความลึก → job): แถวต่ำสุดที่วางได้ = ใต้ req ทุกตัว · แถวไหนเต็ม MAX_LANES เลื่อนลงแถวถัดไป
    const sorted = [...list].sort((a, c) => depth.get(a.id)! - depth.get(c.id)! || a.job - c.job || list.indexOf(a) - list.indexOf(c));
    const used = new Map<number, number>();
    let lanes = 1;
    for (const s of sorted) {
      let r = depth.get(s.id)!;
      for (const q of reqsOf(s)) {
        const placed = finalRow.get(q);
        if (placed !== undefined) r = Math.max(r, placed + 1);
      }
      while ((used.get(r) ?? 0) >= MAX_LANES) r++;
      const lane = used.get(r) ?? 0;
      used.set(r, lane + 1);
      finalRow.set(s.id, r);
      nodes.push({ id: s.id, col: col + lane, row: r });
      lanes = Math.max(lanes, lane + 1);
    }
    headers.push({ label: b ? branches[b]! : "พื้นฐาน", col, span: lanes });
    col += lanes;
  }
  const edges = skills.flatMap((s) => reqsOf(s).filter((r) => byId.has(r)).map((r) => ({ from: r, to: s.id })));
  return { nodes, edges, cols: col, rows: Math.max(0, ...nodes.map((n) => n.row)) + 1, headers };
}
